import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Camera, AlertTriangle, CheckCircle, Shield, Navigation, Phone, Mail, User, ChevronDown, ChevronUp, Loader2, X, Map as MapIcon, Plus, Image, Trash2, FileText, Download, Send } from 'lucide-react';
import { Button } from './ui/button';
import PageTourButton from './PageTourButton';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

const SITUATION_LABELS = {
  swarm_tree_branch: 'Swarm on tree/branch',
  swarm_fence_post: 'Swarm on fence/post',
  swarm_ground_level: 'Ground-level swarm',
  swarm_elevated: 'Elevated swarm',
  swarm_building_exterior: 'Building exterior',
  bees_in_wall: 'Bees in wall cavity',
  bees_in_roof: 'Bees in roof space',
  bees_in_chimney: 'Bees in chimney',
  bees_in_ground: 'Bees in ground',
  established_colony: 'Established colony',
  other: 'Other',
};

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800 border-green-300',
  allocated: 'bg-blue-100 text-blue-800 border-blue-300',
  on_the_way: 'bg-amber-100 text-amber-800 border-amber-300',
  arrived: 'bg-purple-100 text-purple-800 border-purple-300',
  collected: 'bg-teal-100 text-teal-800 border-teal-300',
  swarm_departed: 'bg-stone-100 text-stone-600 border-stone-300',
  cancelled: 'bg-red-100 text-red-600 border-red-300',
};

const SwarmPhotoThumb = ({ path, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    const t = localStorage.getItem('token');
    axios.get(`${API}/api/files/${path}`, { responseType: 'blob', headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then(r => setSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, [path]);
  if (!src) return <div className="w-16 h-16 bg-stone-100 rounded-lg animate-pulse" />;
  return <button onClick={onClick} className="w-16 h-16 rounded-lg overflow-hidden border border-stone-200 hover:border-amber-400 transition-colors"><img src={src} alt="" className="w-full h-full object-cover" /></button>;
};

export default function SwarmCollectionsView() {
  const [tab, setTab] = useState('available');
  const [available, setAvailable] = useState([]);
  const [myJobs, setMyJobs] = useState({ assigned: [], standby: [] });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [claimETA, setClaimETA] = useState('');
  const [claimDate, setClaimDate] = useState('');
  const [claiming, setClaiming] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidDesc, setBidDesc] = useState('');
  const [bidRepair, setBidRepair] = useState(false);
  const [bidHours, setBidHours] = useState('');
  const [bidding, setBidding] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const generateInvoice = async (swarm) => {
    try {
      const res = await axios.get(`${API}/api/swarm-collections/${swarm.id}/invoice-data`, { headers });
      const inv = res.data;
      const ref = inv.ref;
      const date = inv.collected_date;
      const reportDate = inv.reported_date;
      const situationLabel = inv.situation_label;
      const bkLogoHtml = inv.bk_logo_id ? `<img src="${API}/api/uploads/${inv.bk_logo_id}" style="max-height:48px;max-width:120px;object-fit:contain;margin-bottom:4px;display:block" alt="" />` : '';
      const bkContactParts = [inv.bk_phone, inv.bk_email, inv.bk_website].filter(Boolean).join(' · ');
      const invoiceHTML = `<!DOCTYPE html><html><head><title>Invoice ${ref}</title><style>
        body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:40px;color:#292524;line-height:1.6}
        .inv{max-width:700px;margin:0 auto;border:1px solid #e7e5e4;border-radius:12px;padding:40px}
        .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #FDE68A}
        .brand{font-size:22px;font-weight:800;color:#78350F}
        .brand-sub{font-size:11px;color:#78716c;margin-top:2px}
        .badge{background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 16px;text-align:right}
        .badge .ref{font-size:20px;font-weight:800;color:#78350F;letter-spacing:1px}
        .badge .date{font-size:12px;color:#78716c}
        table{width:100%;border-collapse:collapse;margin:24px 0}
        th{text-align:left;padding:10px 12px;background:#FFFBEB;color:#78350F;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #FDE68A}
        td{padding:10px 12px;border-bottom:1px solid #e7e5e4;font-size:14px}
        .total-row td{border-top:2px solid #292524;font-weight:800;font-size:16px;padding-top:14px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:24px 0}
        .meta-box{background:#fafaf9;border:1px solid #e7e5e4;border-radius:8px;padding:14px}
        .meta-box h4{margin:0 0 8px;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#78716c}
        .meta-box p{margin:2px 0;font-size:13px}
        .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e7e5e4;text-align:center;font-size:11px;color:#a8a29e}
        @media print{body{padding:20px}.inv{border:none;padding:0}@page{margin:15mm}}
      </style></head><body>
      <div class="inv">
        <div class="header">
          <div>${bkLogoHtml}<div class="brand">${inv.bk_business_name}</div>${inv.bk_address ? `<div class="brand-sub">${inv.bk_address}</div>` : ''}${bkContactParts ? `<div class="brand-sub">${bkContactParts}</div>` : ''}</div>
          <div class="badge"><div style="font-size:11px;color:#78716c">INVOICE</div><div class="ref">${ref}</div><div class="date">${date}</div></div>
        </div>
        <div class="meta">
          <div class="meta-box"><h4>Service Location</h4><p>${inv.address || inv.suburb || '—'}</p>${reportDate ? `<p style="font-size:11px;color:#78716c">Reported: ${reportDate}</p>` : ''}</div>
          <div class="meta-box"><h4>Client</h4><p>${inv.reporter_name || '—'}</p><p>${inv.reporter_phone || ''}</p><p>${inv.reporter_email || ''}</p></div>
        </div>
        <table>
          <thead><tr><th>Description</th><th>Details</th><th style="text-align:right">Amount</th></tr></thead>
          <tbody>
            <tr><td><strong>${situationLabel} Removal</strong></td><td>${inv.suburb || ''}</td><td style="text-align:right">${inv.amount_str}</td></tr>
            ${inv.includes_repair ? '<tr><td>Includes repair/patching</td><td>—</td><td style="text-align:right">Included</td></tr>' : ''}
            <tr class="total-row"><td colspan="2">Total</td><td style="text-align:right">${inv.amount_str}</td></tr>
          </tbody>
        </table>
        <div class="footer">Facilitated by Thrive Beekeeping Management · ${ref}</div>
      </div>
      <script>window.print();</script>
      </body></html>`;
      const w = window.open('', '_blank');
      w.document.write(invoiceHTML);
      w.document.close();
    } catch (err) {
      toast.error('Failed to generate invoice');
    }
  };

  const downloadInvoicePdf = async (reportId) => {
    setDownloadingPdf(reportId);
    try {
      const res = await axios.get(`${API}/api/swarm-collections/${reportId}/invoice-pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `Invoice_${reportId.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to download invoice';
      toast.error(msg);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const emailInvoice = async (reportId) => {
    setSendingInvoice(reportId);
    try {
      const res = await axios.post(`${API}/api/swarm-collections/${reportId}/send-invoice`, {}, { headers });
      toast.success(res.data.message || 'Invoice emailed!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send invoice';
      toast.error(msg);
    } finally {
      setSendingInvoice(null);
    }
  };

  const fetchData = async () => {
    try {
      const [avail, jobs] = await Promise.all([
        axios.get(`${API}/api/swarm-collections/available`, { headers }),
        axios.get(`${API}/api/swarm-collections/my-jobs`, { headers }),
      ]);
      setAvailable(avail.data || []);
      setMyJobs(jobs.data || { assigned: [], standby: [] });
      try {
        const prof = await axios.get(`${API}/api/swarm-collections/my-profile`, { headers });
        setMyProfile(prof.data);
      } catch {}
      try {
        const hist = await axios.get(`${API}/api/swarm-collections/my-history`, { headers });
        setHistory(hist.data || []);
      } catch {}
      try {
        const lb = await axios.get(`${API}/api/swarm-collections/leaderboard`, { headers });
        setLeaderboard(lb.data || []);
      } catch {}
    } catch {
      toast.error('Failed to load swarm data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, []);

  const handleClaim = async (reportId) => {
    if (!claimETA) return toast.error('Please enter your estimated arrival time');
    setClaiming(reportId);
    try {
      await axios.post(`${API}/api/swarm-collections/${reportId}/claim`, { estimated_arrival: claimETA, planned_date: claimDate || new Date().toISOString().split('T')[0] }, { headers });
      toast.success('Swarm claimed! Contact the reporter to confirm.');
      setExpandedId(reportId);
      setTab('my-jobs');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim swarm');
    } finally {
      setClaiming(null);
      setClaimETA('');
    }
  };

  const handleRelease = async (reportId) => {
    try {
      await axios.post(`${API}/api/swarm-collections/${reportId}/release`, { reason: releaseReason }, { headers });
      toast.success('Swarm released');
      setReleaseReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release');
    }
  };

  const handleStandby = async (reportId) => {
    try {
      await axios.post(`${API}/api/swarm-collections/${reportId}/standby`, {}, { headers });
      toast.success('Added to standby list');
      fetchData();
    } catch {
      toast.error('Failed to join standby');
    }
  };

  const handleBid = async (reportId) => {
    if (!bidAmount || parseFloat(bidAmount) <= 0) return toast.error('Enter a valid amount');
    setBidding(reportId);
    try {
      await axios.post(`${API}/api/swarm-collections/${reportId}/bid`, {
        amount: parseFloat(bidAmount), price_type: 'fixed', description: bidDesc,
        includes_repair: bidRepair, estimated_hours: parseFloat(bidHours) || 0,
      }, { headers });
      toast.success('Quote submitted! The reporter will review bids.');
      setBidAmount(''); setBidDesc(''); setBidRepair(false); setBidHours('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bid');
    } finally { setBidding(null); }
  };

  const handleStatusUpdate = async (reportId, status) => {
    // Require confirmation for terminal statuses
    if (['swarm_departed', 'unable_to_collect', 'cancelled'].includes(status)) {
      setConfirmAction({ reportId, status });
      return;
    }
    await doStatusUpdate(reportId, status);
  };

  const doStatusUpdate = async (reportId, status) => {
    setConfirmAction(null);
    try {
      await axios.put(`${API}/api/swarm-collections/${reportId}/status`, { status }, { headers });
      toast.success(`Status updated: ${status.replace(/_/g, ' ')}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const renderSwarmCard = (swarm, isMyJob = false) => {
    const expanded = expandedId === swarm.id;
    const isAllocated = swarm.status === 'allocated';
    const hasBid = swarm.my_bid_amount != null;
    return (
      <Card key={swarm.id} className={`border ${isMyJob ? 'border-blue-200' : hasBid ? 'border-amber-300' : 'border-stone-200'} transition-shadow hover:shadow-md`}>
        <CardContent className="p-4">
          <div
            className="flex items-start justify-between cursor-pointer"
            onClick={() => setExpandedId(expanded ? null : swarm.id)}
            data-testid={`swarm-card-header-${swarm.id}`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${STATUS_COLORS[swarm.status] || 'bg-stone-100'}`}>
                  {swarm.status?.replace(/_/g, ' ').toUpperCase()}
                </span>
                <span className="text-sm font-bold text-stone-800">{SITUATION_LABELS[swarm.situation_type] || swarm.situation_type}</span>
                {hasBid && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300" data-testid={`bid-badge-${swarm.id}`}>
                    Your Quote: ${swarm.my_bid_amount}
                  </span>
                )}
                {swarm.is_paid_removal && !hasBid && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    Paid Removal
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 text-sm text-stone-600">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-amber-600" />{swarm.suburb || 'Unknown'}</span>
                {swarm.distance_km !== undefined && <span className="text-amber-700 font-medium">~{swarm.distance_km}km</span>}
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{swarm.created_at ? formatDistanceToNow(new Date(swarm.created_at), { addSuffix: true }) : ''}</span>
                {(swarm.ref || swarm.id) && <span className="text-[10px] font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{swarm.ref || `SW-${swarm.id?.slice(0, 6).toUpperCase()}`}</span>}
              </div>
              {swarm.accepted_bid_amount && isMyJob && (
                <div className="mt-2 bg-green-50 border border-green-200 rounded-md px-2 py-1 flex items-center gap-1.5 w-fit">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs font-bold text-green-800">Agreed Price: ${swarm.accepted_bid_amount}</span>
                </div>
              )}
              {swarm.height_estimate && <p className="text-xs text-stone-500 mt-1">Height: {swarm.height_estimate}</p>}
            </div>
            <div className="ml-2 p-1 text-stone-400">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>

          {/* Photo strip */}
          {swarm.photos?.length > 0 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {swarm.photos.map((p, i) => <SwarmPhotoThumb key={i} path={p} onClick={() => setLightbox(p)} />)}
            </div>
          )}

          {expanded && (
            <div className="mt-4 pt-3 border-t border-stone-100 space-y-3">
              {swarm.description && <p className="text-sm text-stone-600"><strong>Description:</strong> {swarm.description}</p>}
              {swarm.access_notes && <p className="text-sm text-stone-600"><strong>Access:</strong> {swarm.access_notes}</p>}
              {swarm.last_seen && <p className="text-sm text-stone-600"><strong>Last seen:</strong> {swarm.last_seen}</p>}
              {swarm.standby_count > 0 && <p className="text-xs text-stone-500">{swarm.standby_count} on standby</p>}


              {/* Paid removal — submit quote */}
              {['available', 'bidding'].includes(swarm.status) && !isMyJob && swarm.is_paid_removal && !hasBid && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-blue-800">Submit a quote for this paid removal</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-stone-500">Your price ($)</label>
                      <Input type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)} placeholder="e.g. 250" className="text-sm" data-testid="bid-amount" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500">Est. hours</label>
                      <Input type="number" value={bidHours} onChange={e => setBidHours(e.target.value)} placeholder="e.g. 2" className="text-sm" />
                    </div>
                  </div>
                  <Input value={bidDesc} onChange={e => setBidDesc(e.target.value)} placeholder="Describe what's included in your quote..." className="text-sm" />
                  <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
                    <input type="checkbox" checked={bidRepair} onChange={e => setBidRepair(e.target.checked)} className="rounded" />
                    Includes repair/patching after removal
                  </label>
                  <Button onClick={() => handleBid(swarm.id)} disabled={bidding === swarm.id} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-bid-btn">
                    {bidding === swarm.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Submit Quote
                  </Button>
                  {swarm.bid_count > 0 && <p className="text-xs text-blue-600 text-center">{swarm.bid_count} quote(s) already submitted</p>}
                </div>
              )}

              {/* Already bid — show confirmation */}
              {['available', 'bidding'].includes(swarm.status) && !isMyJob && swarm.is_paid_removal && hasBid && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Your quote of ${swarm.my_bid_amount} has been submitted</p>
                    <p className="text-xs text-amber-600">The reporter will review all quotes and choose. You'll be notified if selected.</p>
                  </div>
                </div>
              )}

              {/* Claim controls for available swarms */}
              {swarm.status === 'available' && !isMyJob && !swarm.is_paid_removal && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-green-800">Claim this swarm collection</p>
                  <p className="text-xs text-green-700 bg-green-100 rounded-md p-2">This is a <strong>free collection</strong> service. Do not charge the reporter.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-stone-500">Collection date</label>
                      <Input type="date" value={claimDate} onChange={e => setClaimDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="text-sm" data-testid="claim-date-input" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500">Estimated arrival time</label>
                      <Input value={claimETA} onChange={e => setClaimETA(e.target.value)} placeholder="e.g. 3:30pm" className="text-sm" data-testid="claim-eta-input" />
                    </div>
                  </div>
                  <Button onClick={() => handleClaim(swarm.id)} disabled={claiming === swarm.id} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="claim-swarm-btn">
                    {claiming === swarm.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Claim This Swarm
                  </Button>
                </div>
              )}

              {/* Allocated — show standby button */}
              {swarm.status === 'allocated' && !isMyJob && (
                <Button onClick={() => handleStandby(swarm.id)} variant="outline" className="w-full border-amber-300 text-amber-700">
                  Join Standby List
                </Button>
              )}

              {/* My job controls */}
              {isMyJob && (
                <div className="space-y-3">
                  {/* Reporter contact (only shown to assigned beekeeper) */}
                  {swarm.reporter_name && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="font-medium text-blue-800 text-sm mb-2">Reporter Contact</p>
                      <div className="space-y-1 text-sm text-blue-700">
                        <p className="flex items-center gap-2"><User className="h-3.5 w-3.5" />{swarm.reporter_name}</p>
                        {swarm.reporter_phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /><a href={`tel:${swarm.reporter_phone}`} className="underline">{swarm.reporter_phone}</a></p>}
                        {swarm.reporter_email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{swarm.reporter_email}</p>}
                      </div>
                      {swarm.address && <p className="text-sm text-blue-600 mt-2"><strong>Address:</strong> {swarm.address}</p>}
                    </div>
                  )}

                  {/* Status update buttons */}
                  <div className="flex flex-wrap gap-2">
                    {swarm.status === 'allocated' && (
                      <Button onClick={() => handleStatusUpdate(swarm.id, 'on_the_way')} className="bg-amber-500 hover:bg-amber-600 text-white flex-1" data-testid="status-on-way">
                        <Navigation className="h-4 w-4 mr-1" /> On My Way
                      </Button>
                    )}
                    {swarm.status === 'on_the_way' && (
                      <Button onClick={() => handleStatusUpdate(swarm.id, 'arrived')} className="bg-purple-500 hover:bg-purple-600 text-white flex-1" data-testid="status-arrived">
                        Arrived
                      </Button>
                    )}
                    {(swarm.status === 'arrived' || swarm.status === 'on_the_way') && (
                      <Button onClick={() => handleStatusUpdate(swarm.id, 'collected')} className="bg-teal-600 hover:bg-teal-700 text-white flex-1" data-testid="status-collected">
                        <CheckCircle className="h-4 w-4 mr-1" /> Collected
                      </Button>
                    )}
                    {['allocated', 'on_the_way'].includes(swarm.status) && (
                      <>
                        <Button onClick={() => handleStatusUpdate(swarm.id, 'swarm_departed')} variant="outline" className="text-stone-600 flex-1">
                          Swarm Departed
                        </Button>
                        <div className="w-full flex gap-2 mt-1">
                          <Input value={releaseReason} onChange={e => setReleaseReason(e.target.value)} placeholder="Reason for releasing..." className="flex-1 text-sm" />
                          <Button onClick={() => handleRelease(swarm.id)} variant="outline" className="text-red-600 border-red-300">Release</Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) return <div className="p-8 text-center text-stone-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Loading swarm reports...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Shield className="h-6 w-6 text-amber-600" />
          Swarm Rescues
        </h2>
        <PageTourButton pageView="swarm-collections" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-stone-100 rounded-lg p-1">
        <button onClick={() => setTab('available')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'available' ? 'bg-white shadow text-amber-700' : 'text-stone-600'}`} data-testid="tab-available">
          Available ({available.length})
        </button>
        <button onClick={() => setTab('my-jobs')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'my-jobs' ? 'bg-white shadow text-blue-700' : 'text-stone-600'}`} data-testid="tab-my-jobs">
          My Jobs ({myJobs.assigned.length})
        </button>
        <button onClick={() => setTab('map')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'map' ? 'bg-white shadow text-green-700' : 'text-stone-600'}`} data-testid="tab-map">
          Map
        </button>
        <button onClick={() => setTab('history')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'history' ? 'bg-white shadow text-stone-700' : 'text-stone-600'}`} data-testid="tab-history">
          History ({history.length})
        </button>
      </div>

      {/* Available Tab */}
      {tab === 'available' && (
        <div className="space-y-3">
          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3">
                <h3 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                  <span>🏆</span> Top Collectors
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {leaderboard.map((b, i) => (
                    <div key={i} className="flex-shrink-0 bg-white rounded-lg border border-amber-200 p-2 min-w-[120px] text-center">
                      <div className="text-xs font-bold text-stone-800">{b.name}</div>
                      <div className="flex justify-center gap-0.5 mt-0.5">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} className={`text-xs ${s <= Math.round(b.average_rating) ? 'text-amber-400' : 'text-stone-200'}`}>★</span>
                        ))}
                      </div>
                      <div className="text-[10px] text-stone-500 mt-0.5">{b.collections} collected · {b.total_ratings} ratings</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {available.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Shield className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="font-medium">No swarm reports in your area</p>
              <p className="text-sm mt-1">Make sure your collection profile is set up with your location and radius</p>
            </div>
          ) : (
            available.map(s => renderSwarmCard(s))
          )}
        </div>
      )}

      {/* My Jobs Tab */}
      {tab === 'my-jobs' && (
        <div className="space-y-3">
          {myJobs.assigned.length === 0 && myJobs.standby.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <CheckCircle className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="font-medium">No active collections</p>
              <p className="text-sm mt-1">Claim a swarm from the Available tab</p>
            </div>
          ) : (
            <>
              {myJobs.assigned.map(s => renderSwarmCard(s, true))}
              {myJobs.standby.length > 0 && (
                <>
                  <h3 className="text-sm font-bold text-stone-600 pt-2">On Standby</h3>
                  {myJobs.standby.map(s => (
                    <Card key={s.id} className="border-stone-200 opacity-70">
                      <CardContent className="p-3">
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">STANDBY</span>
                        <span className="text-sm ml-2">{SITUATION_LABELS[s.situation_type]} — {s.suburb}</span>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Map Tab */}
      {tab === 'map' && (
        <div className="rounded-xl overflow-hidden border border-stone-200" style={{ height: '500px' }}>
          {myProfile?.base_latitude ? (
            <MapContainer
              center={[myProfile.base_latitude, myProfile.base_longitude]}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              {/* Base location marker */}
              <Marker position={[myProfile.base_latitude, myProfile.base_longitude]}
                icon={L.divIcon({ className: '', html: '<div style="background:#D97706;color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">🏠</div>', iconSize: [28, 28], iconAnchor: [14, 14] })}
              >
                <Popup>Your Base Location</Popup>
              </Marker>
              {/* Radius circle */}
              <Circle center={[myProfile.base_latitude, myProfile.base_longitude]} radius={(myProfile.collection_radius_km || 25) * 1000} pathOptions={{ color: '#D97706', fillColor: '#FEF3C7', fillOpacity: 0.15, weight: 2, dashArray: '8 4' }} />
              {/* Swarm markers */}
              {available.filter(s => s.latitude && s.longitude).map(s => (
                <Marker key={s.id} position={[s.latitude, s.longitude]}
                  icon={L.divIcon({ className: '', html: `<div style="background:${s.status === 'available' ? '#16A34A' : '#2563EB'};color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">🐝</div>`, iconSize: [24, 24], iconAnchor: [12, 12] })}
                  eventHandlers={{ click: () => { setTab('available'); setExpandedId(s.id); } }}
                >
                  <Popup>
                    <div style={{ minWidth: 180 }}>
                      <strong>{SITUATION_LABELS[s.situation_type] || s.situation_type}</strong>
                      <br />{s.suburb} — ~{s.distance_km}km
                      {s.height_estimate && <><br />Height: {s.height_estimate}</>}
                      <br /><span style={{ fontSize: 11, color: '#78716C' }}>{s.created_at ? formatDistanceToNow(new Date(s.created_at), { addSuffix: true }) : ''}</span>
                      <br /><button onClick={() => { setTab('available'); setExpandedId(s.id); }} style={{ marginTop: 6, background: '#16A34A', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' }}>View & Claim</button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center bg-stone-100 text-stone-500">
              <div className="text-center">
                <MapIcon className="h-12 w-12 mx-auto text-stone-300 mb-2" />
                <p className="font-medium">Set your base location first</p>
                <p className="text-sm mt-1">Go to Settings → Swarm Rescue to set your location</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Clock className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="font-medium">No collection history yet</p>
              <p className="text-sm mt-1">Completed, departed, and cancelled swarms will appear here</p>
            </div>
          ) : (
            history.map(s => (
              <Card key={s.id} className={`border-stone-200 ${s.status === 'collected' ? 'border-l-4 border-l-teal-500' : 'border-l-4 border-l-stone-300 opacity-80'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${STATUS_COLORS[s.status] || 'bg-stone-100'}`}>
                      {s.status?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span className="text-sm font-bold text-stone-800">{SITUATION_LABELS[s.situation_type] || s.situation_type}</span>
                    <span className="text-xs text-stone-500">{s.suburb || s.address?.slice(0, 30)}</span>
                    <span className="text-[10px] font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{s.ref || `SW-${s.id?.slice(0, 6).toUpperCase()}`}</span>
                    {s.accepted_bid_amount && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">${s.accepted_bid_amount}</span>}
                    {s.collected_at && <span className="text-xs text-teal-600 ml-auto">Collected {formatDistanceToNow(new Date(s.collected_at), { addSuffix: true })}</span>}
                    {!s.collected_at && s.updated_at && <span className="text-xs text-stone-400 ml-auto">{formatDistanceToNow(new Date(s.updated_at), { addSuffix: true })}</span>}
                  </div>
                  {s.reporter_name && <p className="text-xs text-stone-500 mt-1">Reporter: {s.reporter_name} — {s.address?.slice(0, 50)}</p>}

                  {/* Invoice buttons for completed paid removals */}
                  {s.status === 'collected' && s.is_paid_removal && s.accepted_bid_amount && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                        onClick={() => downloadInvoicePdf(s.id)}
                        disabled={downloadingPdf === s.id}
                        data-testid={`invoice-pdf-btn-${s.id}`}
                      >
                        {downloadingPdf === s.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                        Download PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        onClick={() => emailInvoice(s.id)}
                        disabled={sendingInvoice === s.id}
                        data-testid={`invoice-email-btn-${s.id}`}
                      >
                        {sendingInvoice === s.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                        Email Invoice
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-stone-500 hover:text-stone-700"
                        onClick={() => generateInvoice(s)}
                        data-testid={`invoice-print-btn-${s.id}`}
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" /> Print
                      </Button>
                      {s.invoice_sent_at && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Sent
                        </span>
                      )}
                    </div>
                  )}

                  {/* Gallery section for collected jobs */}
                  {s.status === 'collected' && (
                    <GallerySection reportId={s.id} headers={headers} existingPhotos={s.gallery || []} onUpdate={fetchData} />
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmAction(null)}>
          <Card className="max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-stone-800">Are you sure?</h3>
              <p className="text-stone-600 mt-2">
                {confirmAction.status === 'swarm_departed' && 'Mark this swarm as departed? This will close the job and you cannot undo it.'}
                {confirmAction.status === 'unable_to_collect' && 'Mark as unable to collect? The job will be closed.'}
                {confirmAction.status === 'cancelled' && 'Cancel this swarm report? This cannot be undone.'}
              </p>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setConfirmAction(null)} variant="outline" className="flex-1">No, Go Back</Button>
                <Button onClick={() => doStatusUpdate(confirmAction.reportId, confirmAction.status)} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  Yes, Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white"><X className="h-8 w-8" /></button>
          <SwarmLightbox path={lightbox} />
        </div>
      )}
    </div>
  );
}


const GallerySection = ({ reportId, headers, existingPhotos, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [photos, setPhotos] = useState(existingPhotos || []);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState('after');
  const [caption, setCaption] = useState('');
  const fileRef = React.useRef(null);

  const loadGallery = async () => {
    try {
      const res = await axios.get(`${API}/api/swarm-collections/${reportId}/gallery`, { headers });
      setPhotos(res.data || []);
    } catch {}
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.slice(0, 5).forEach(f => fd.append('photos', f));
      fd.append('photo_type', photoType);
      fd.append('caption', caption);
      await axios.post(`${API}/api/swarm-collections/${reportId}/gallery`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photos added to gallery!');
      setCaption('');
      loadGallery();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/api/swarm-collections/${reportId}/gallery/${photoId}`, { headers });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-stone-100">
      <button onClick={() => { setExpanded(!expanded); if (!expanded && photos.length === 0) loadGallery(); }} className="flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800" data-testid={`gallery-toggle-${reportId}`}>
        <Image className="h-4 w-4" />
        Portfolio Photos ({photos.length})
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {/* Existing photos */}
          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map(p => (
                <div key={p.id} className="relative group">
                  <SwarmPhotoThumb path={p.path} onClick={() => {}} />
                  <span className={`absolute -top-1 -left-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${p.type === 'before' ? 'bg-amber-500 text-white' : p.type === 'during' ? 'bg-blue-500 text-white' : 'bg-teal-500 text-white'}`}>
                    {p.type}
                  </span>
                  <button onClick={() => handleDelete(p.id)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                  {p.caption && <p className="text-[9px] text-stone-500 mt-0.5 max-w-[64px] truncate">{p.caption}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Upload controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {['before', 'during', 'after'].map(t => (
                <button key={t} onClick={() => setPhotoType(t)} className={`px-2 py-1 text-[10px] font-medium rounded-full ${photoType === t ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-600'}`}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            <Input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)" className="h-7 text-xs flex-1 min-w-[120px]" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 border border-dashed border-teal-300 rounded-lg px-2.5 py-1.5 hover:bg-teal-50 transition-colors" data-testid={`gallery-upload-${reportId}`}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {uploading ? 'Uploading...' : 'Add Photos'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
          </div>
        </div>
      )}
    </div>
  );
};


const SwarmLightbox = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const t = localStorage.getItem('token');
    axios.get(`${API}/api/files/${path}`, { responseType: 'blob', headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then(r => setSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, [path]);
  if (!src) return <div className="w-64 h-64 bg-stone-800 animate-pulse rounded-xl" />;
  return <img src={src} alt="" className="max-w-full max-h-[80vh] object-contain rounded-xl" />;
};
